// FILE: src/components/shared/ImageLightbox.js
//
// ImageLightbox — generic modal overlay showing a single piece of artwork
// at large size with a title, optional subtitle, and optional status chip.
// Used by the Journey roadmap (and reusable for any art zoom need).
//
// Controlled: caller passes `item` ({ src, alt, title, subtitle, chip,
// chipTone }) or null, plus onClose. Closes on backdrop click, the X
// button, or Escape. Functional component, hooks only. No Redux.

import React, { useEffect } from 'react';
import styles from './ImageLightbox.module.css';

export default function ImageLightbox({ item, onClose }) {
  useEffect(() => {
    if (!item) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [item, onClose]);

  if (!item) return null;

  const chipClass = [
    styles.chip,
    item.chipTone === 'good' ? styles.chipGood : '',
    item.chipTone === 'active' ? styles.chipActive : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={styles.backdrop} onClick={onClose} data-testid="image-lightbox-backdrop">
      <div
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label={item.title || item.alt || 'Image preview'}
        onClick={(e) => e.stopPropagation()}
        data-testid="image-lightbox"
      >
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Close image view"
          data-testid="image-lightbox-close"
        >&times;</button>
        <img className={styles.art} src={item.src} alt={item.alt || item.title || ''} draggable="false" />
        <div className={styles.meta}>
          <div className={styles.titleRow}>
            {item.title && <span className={styles.title}>{item.title}</span>}
            {item.chip && <span className={chipClass}>{item.chip}</span>}
          </div>
          {item.subtitle && <p className={styles.subtitle}>{item.subtitle}</p>}
        </div>
      </div>
    </div>
  );
}
